import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { formatWholeUah } from '../../helpers/taxPlanner/money';
import Badge from '../Badge';
import Banner from '../Banner';
import GhostButton from '../GhostButton';
import { RowSpinner } from '../Loader/Loader';
import PrimaryButton from '../PrimaryButton';
import AlertTriangle from '../icons/AlertTriangle';
import ChartIcon from '../icons/ChartIcon';
import DocIcon from '../icons/DocIcon';
import PlannerDialog from './PlannerDialog';
import { pluralForm } from '../../helpers/pluralForm';

const TITLE_ID = 'tax-planner-entry-title';

// Mock `.modal-icon`: 44×44 tile; warn tone for the stale window.
export function DialogIcon({ tone = 'accent', children }) {
  return (
    <div
      data-testid="dialog-icon"
      className={clsx(
        'flex h-11 w-11 flex-none items-center justify-center rounded-xl',
        tone === 'warn' ? 'bg-warn-bg text-warn' : 'bg-surface-alt text-accent-solid',
      )}>
      {children}
    </div>
  );
}


const isoToDotted = (iso) => iso.split('-').reverse().join('.');

const MINUS = '−';
const signed = (value) => (String(value).startsWith('-') ? `${MINUS}${formatWholeUah(String(value).slice(1))}` : formatWholeUah(value));

function EntryWindow({ variant, snapshot, staleAccounts, pick, totals, now, onRetry, onProceed, onAcknowledgeStale, onNavigate }) {
  const { t, i18n } = useTranslation();
  const k = (key, opts) => t(`taxPlanner.entry.${key}`, opts);
  const year = snapshot?.taxYear ?? now.getFullYear();
  const broker = (a) => `${t(`taxPlanner.brokers.${a.dataProvider}`, { defaultValue: a.dataProvider })} ${a.originalId}`;
  const accounts = snapshot?.accounts || [];
  const accountList = staleAccounts?.length ? staleAccounts : accounts;

  const warning = staleAccounts?.length ? (
    <Banner tone="warn">{k('stale.warning', { accounts: staleAccounts.map(broker).join(', ') })}</Banner>
  ) : null;

  const list = (rows) => (
    <ul className="flex flex-col gap-2">
      {rows.map(({ account, badge, tone }) => (
        <li key={account.investingAccountId} className="flex items-center justify-between gap-3 text-sm text-text">
          <span>{broker(account)}</span>
          <Badge size="md" tone={tone}>{badge}</Badge>
        </li>
      ))}
    </ul>
  );

  const staleBadge = (a) =>
    a.dataAsOf
      ? k(`stale.badgeAsOf_${pluralForm(a.dataAgeDays, i18n.language)}`, { date: isoToDotted(a.dataAsOf), count: a.dataAgeDays })
      : k('stale.badgeNone');

  let icon = null;
  let title;
  let text = null;
  let body = null;
  let actions = null;
  let eyebrow = null;
  let footnote = null;

  switch (variant) {
    case 'loading':
      icon = <RowSpinner />;
      title = k('loading.title');
      text = k('loading.text');
      break;
    case 'error':
      title = k('error.title');
      text = k('error.text');
      actions = (
        <PrimaryButton size="modal" autoFocus onClick={onRetry}>
          {k('error.retry')}
        </PrimaryButton>
      );
      break;
    case 'rate-limited':
      title = k('rateLimited.title');
      text = k('rateLimited.text');
      break;
    case 'empty':
      icon = <DialogIcon><DocIcon /></DialogIcon>;
      title = k('empty.title');
      text = k('empty.text', { year });
      actions = (
        <PrimaryButton size="modal" autoFocus onClick={() => onNavigate('upload')}>
          {k('empty.upload')}
        </PrimaryButton>
      );
      break;
    case 'recalculating':
    case 'recalculating-long': {
      const long = variant === 'recalculating-long';
      title = k(long ? 'recalculatingLong.title' : 'recalculating.title');
      text = k(long ? 'recalculatingLong.text' : 'recalculating.text');
      body = list((snapshot?.accounts || []).map((account) => ({
        account,
        tone: long ? 'warn' : 'muted',
        badge: k(long ? 'recalculatingLong.badge' : 'recalculating.badge'),
      })));
      break;
    }
    case 'phantom-year':
      title = k('phantomYear.title');
      text = k('phantomYear.text');
      body = list(accounts.map((account) => ({ account, tone: 'warn', badge: k('phantomYear.badge') })));
      actions = (
        <PrimaryButton size="modal" autoFocus onClick={() => onNavigate('clarify')}>
          {k('phantomYear.clarify')}
        </PrimaryButton>
      );
      break;
    case 'stale':
      icon = (
        <DialogIcon tone="warn">
          <AlertTriangle />
        </DialogIcon>
      );
      title = k('stale.title');
      text = k('stale.text');
      body = list(accountList.map((account) => ({ account, tone: 'warn', badge: staleBadge(account) })));
      actions = (
        <>
          <PrimaryButton size="modal" autoFocus onClick={() => onNavigate('upload')}>
            {k('stale.update')}
          </PrimaryButton>
          <GhostButton size="modal" onClick={onAcknowledgeStale}>
            {k('stale.go')}
          </GhostButton>
        </>
      );
      break;
    case 'default':
      icon = <DialogIcon><ChartIcon /></DialogIcon>;
      eyebrow = k('eyebrow', { year });
      title = k('default.title');
      body = (
        <div>
          <p className="text-xs text-text-muted">{k('default.label')}</p>
          <p className="num text-[34px] font-bold tracking-[-.03em] text-success-ink">
            {k('default.amount', { amount: formatWholeUah(totals?.reducedBy ?? '0') })}
          </p>
          <p className="text-xs text-text-muted">
            {k(`default.pick_${pluralForm(pick?.summary?.count ?? 0, i18n.language)}`, { count: pick?.summary?.count ?? 0 })}
          </p>
        </div>
      );
      actions = (
        <PrimaryButton size="modal" autoFocus onClick={onProceed}>
          {k('default.proceed')}
        </PrimaryButton>
      );
      footnote = k('default.hint');
      break;
    default: {
      // cannot-reduce
      icon = <DialogIcon><ChartIcon /></DialogIcon>;
      eyebrow = k('eyebrow', { year });
      title = k('cannotReduce.title');
      const reason = {
        ZERO_TRADING_TAX: k('cannotReduce.reasonNegative', { year, result: signed(totals?.tradingResult ?? '0') }),
        AT_OR_BELOW_TARGET: k('cannotReduce.reasonAtTarget'),
        NO_LOSING_POSITIONS: k('cannotReduce.reasonNoLosing'),
      }[pick?.reason];
      text = reason;
      actions = (
        <PrimaryButton size="modal" autoFocus onClick={onProceed}>
          {k('default.proceed')}
        </PrimaryButton>
      );
    }
  }

  return (
    <PlannerDialog labelledBy={TITLE_ID}>
      {icon}
      {eyebrow && <p className="text-[11px] font-semibold uppercase tracking-[.06em] text-text-muted">{eyebrow}</p>}
      <h2 id={TITLE_ID} className="text-xl font-semibold leading-[1.3] tracking-[-.01em] text-text">
        {title}
      </h2>
      {text && <p className="text-sm text-text-muted">{text}</p>}
      {body}
      {variant !== 'default' && variant !== 'cannot-reduce' ? null : warning}
      {actions && <div className="mt-1 flex flex-wrap gap-2.5">{actions}</div>}
      {footnote && <p className="text-xs text-text-muted">{footnote}</p>}
    </PlannerDialog>
  );
}

export default EntryWindow;
